import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { ReportParagraphsRepository } from '../../services/report-paragraphs-repository';
import type { SpecChapter } from '../../types';
import { GetReportParagraphsApi, type GetReportParagraphsResponse } from '../../apis/report-paragraphs-api';
import _ from 'lodash';

@injectable()
export class ReportParagraphsRepositoryImpl implements ReportParagraphsRepository {
    constructor(
        @inject(GetReportParagraphsApi)
        private _reportParagraphsApi: GetReportParagraphsApi,
    ) {}

    async fetchSpecChapter(taskId: string, abortSignal?: AbortSignal): Promise<SpecChapter[]> {
        const result = await this._reportParagraphsApi.getReportParagraphs({
            task_id: taskId,
        }, abortSignal);

        if (result.chapters.length === 0) {
            return [];
        }

        const chapters = result.chapters.map(item => this._convertToSpecChapter(item));

        try {
            // 补充一个 abstract figure 章节
            const abstractFigureChapter = this._addAbstractFigureChapter(chapters);
            chapters.push(abstractFigureChapter);
        } catch (error) {
            // 如果找不到 figures 章节或 abstract figure，就不添加 abstract_figure 章节
            console.warn('Failed to add abstract figure chapter:', error);
        }

        return chapters;
    }

    private _convertToSpecChapter(item: GetReportParagraphsResponse['chapters'][number]): SpecChapter {
        const paragraphs = _.map(item.paragraphs, (paragraph) => ({
            index: paragraph.paragraph_number,
            content: paragraph.paragraph_content,
            splitIndex: paragraph.split_index,
            embodimentNumber: paragraph.embodiment_number,
            embodimentType: paragraph.embodiment_type,
        }));

        const figures = _.map(item.figures, (figure) => ({
            fid: figure.unique_id,
            isAbstract: figure.abstract_figure,
            imageUrl: figure.annotation_image_base64 || figure.image.url,
        }));

        // 如果 figures 中所有的 figure 都不存在 isAbstract 为 true 的情况，则将 figures 里第一个 figure 的 isAbstract 设置为 true
        if (figures && figures.length > 0 && !_.some(figures, (figure) => figure.isAbstract)) {
            figures[0].isAbstract = true;
        }

        return {
            title: item.chapter_type,
            paragraphs: paragraphs,
            figures: figures,
        };
    }

    private _addAbstractFigureChapter(chapters: SpecChapter[]): SpecChapter {
        const chapter = chapters.find(chapter => chapter.title === 'figures');
        if (!chapter) {
            throw new Error('figures chapter not found');
        }

        const figures = chapter.figures;
        if (figures.length === 0) {
            return {
                title: 'abstract_figure',
                paragraphs: [],
                figures: [],
            };
        }

        const abstractFigure = figures.find(figure => figure.isAbstract);
        if (!abstractFigure) {
            throw new Error('abstract figure not found');
        }

        const abstractFigureChapter = {
            title: 'abstract_figure',
            paragraphs: [],
            figures: [abstractFigure],
        };
        return abstractFigureChapter;
    }
}
